import Highcharts from 'highcharts';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { z } from 'zod';
import { LANGUAGE_STORAGE_KEY, type Language, resolveInitialLanguage } from './language';
import { de } from './locales/de';
import { en } from './locales/en';

export { type Language, SUPPORTED_LANGUAGES } from './language';

type SideEffect = (language: Language) => void;
const sideEffects: SideEffect[] = [];

/** Registered by modules that must react to a switch (zod locale, Highcharts lang). Runs on init and on every change. */
export function onLanguageChange(effect: SideEffect) {
	sideEffects.push(effect);
	effect(getLanguage());
}

export function getInitialLanguage(): Language {
	let stored: string | null = null;
	try {
		stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
	} catch {
		stored = null;
	}
	return resolveInitialLanguage({ stored, navigatorLanguage: navigator.language });
}

const initialLanguage = getInitialLanguage();

i18next.use(initReactI18next).init({
	resources: { en: { translation: en }, de: { translation: de } },
	lng: initialLanguage,
	fallbackLng: 'en',
	interpolation: { escapeValue: false },
	react: { useSuspense: false },
	returnNull: false,
});

document.documentElement.lang = initialLanguage;

export function getLanguage(): Language {
	return (i18next.resolvedLanguage as Language | undefined) ?? initialLanguage;
}

export async function setLanguage(language: Language) {
	await i18next.changeLanguage(language);
	try {
		localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
	} catch {
		// Private mode or blocked storage: the choice just does not survive a reload.
	}
	document.documentElement.lang = language;
	for (const effect of sideEffects) effect(language);
}

/** Translation outside React: query option files, hook callbacks, helpers. Always reads the current language. */
export const t = i18next.t.bind(i18next);

export default i18next;

onLanguageChange((language) => {
	z.config(language === 'de' ? z.locales.de() : z.locales.en());
	Highcharts.setOptions({
		lang:
			language === 'de'
				? { locale: 'de', decimalPoint: ',', thousandsSep: '.' }
				: { locale: 'en-GB', decimalPoint: '.', thousandsSep: ',' },
	});
});
